/**
 * Строки интерфейса на трёх языках.
 *
 * Полноценная библиотека i18n здесь избыточна: строк немного, склонений почти
 * нет, а лишняя зависимость тянет плюрализацию, загрузку локалей и своё
 * состояние. Обычного словаря хватает, а язык берётся из адреса (useLanguage).
 */

import { computed, type ComputedRef } from 'vue'
import { useLanguage, type LanguageCode, DEFAULT_LANGUAGE } from './useLanguage'

type Dict = Record<string, string>

const RU: Dict = {
  'nav.projects': 'Проекты',
  'nav.media': 'Медиа',
  'nav.blog': 'Дневник',
  'nav.home': 'Главная',

  'home.kicker': 'журнал pole DUSTAR — 2026',
  'home.title': 'Код и звук, записанные одной рукой',
  'home.lead': 'Личный дневник вайб-кодинга, музыки и визуальных опытов. Без ленты и рекомендаций — только то, что уже сделано и записано.',
  'home.seeProjects': 'Смотреть проекты',
  'home.listen': 'Слушать',
  'home.nowPlaying': 'now playing',
  'home.inProgress': 'в работе',
  'home.lastText': 'последний текст',
  'home.noPosts': 'Записей пока нет.',
  'home.guideTexts': 'Тексты',
  'home.guideClips': 'Клипы',
  'home.guideWorks': 'Работы',
  'home.guideTextsHint': 'разборы и заметки',
  'home.guideClipsHint': 'музыка и видео',
  'home.guideWorksHint': 'инструменты и опыты',
  'home.latestTexts': 'Последние тексты',
  'home.allJournal': 'Весь дневник →',
  'home.clips': 'Клипы',
  'home.allClips': 'Все клипы →',
  'home.noTexts': 'Пока нет текстов.',
  'home.new': 'новое',

  'blog.title': 'Дневник',
  'blog.lead': 'Заметки о процессе: что сломалось, что зазвучало и почему я это переписал.',
  'blog.all': 'все',
  'blog.empty': 'Записей не найдено',
  'blog.records': 'записей',
  'blog.sortNew': 'новые ↓',
  'blog.sortOld': 'старые ↑',

  'article.back': '← к дневнику',
  'article.share': 'Поделиться',
  'article.notFound': 'Статья не найдена',
  'article.notFoundText': 'Запрашиваемая статья не существует или была удалена.',
  'article.backToBlog': 'Вернуться в дневник',
  'article.cover': 'обложка',
  'article.machineTranslated': 'Машинный перевод. Оригинал — на русском.',
  'article.readOriginal': 'Читать оригинал',

  'common.min': 'мин',
  'common.loading': 'Загрузка…',
  'common.language': 'Язык',
  'footer.nav': 'нажмите / для навигации',
}

const EN: Dict = {
  'nav.projects': 'Projects',
  'nav.media': 'Media',
  'nav.blog': 'Journal',
  'nav.home': 'Home',

  'home.kicker': 'pole DUSTAR journal — 2026',
  'home.title': 'Code and sound, written by one hand',
  'home.lead': 'A personal diary of vibe coding, music and visual experiments. No feed, no recommendations — only what is already made and recorded.',
  'home.seeProjects': 'See projects',
  'home.listen': 'Listen',
  'home.nowPlaying': 'now playing',
  'home.inProgress': 'in progress',
  'home.lastText': 'latest writing',
  'home.noPosts': 'Nothing here yet.',
  'home.guideTexts': 'Writing',
  'home.guideClips': 'Clips',
  'home.guideWorks': 'Works',
  'home.guideTextsHint': 'essays and notes',
  'home.guideClipsHint': 'music and video',
  'home.guideWorksHint': 'tools and experiments',
  'home.latestTexts': 'Latest writing',
  'home.allJournal': 'Whole journal →',
  'home.clips': 'Clips',
  'home.allClips': 'All clips →',
  'home.noTexts': 'No writing yet.',
  'home.new': 'new',

  'blog.title': 'Journal',
  'blog.lead': 'Notes on the process: what broke, what finally sounded right, and why I rewrote it.',
  'blog.all': 'all',
  'blog.empty': 'Nothing found',
  'blog.records': 'entries',
  'blog.sortNew': 'newest ↓',
  'blog.sortOld': 'oldest ↑',

  'article.back': '← to journal',
  'article.share': 'Share',
  'article.notFound': 'Article not found',
  'article.notFoundText': 'This article does not exist or has been removed.',
  'article.backToBlog': 'Back to journal',
  'article.cover': 'cover',
  'article.machineTranslated': 'Machine translation. The original is in Russian.',
  'article.readOriginal': 'Read the original',

  'common.min': 'min',
  'common.loading': 'Loading…',
  'common.language': 'Language',
  'footer.nav': 'press / to navigate',
}

const ZH: Dict = {
  'nav.projects': '项目',
  'nav.media': '媒体',
  'nav.blog': '日志',
  'nav.home': '首页',

  'home.kicker': 'pole DUSTAR 日志 — 2026',
  'home.title': '代码与声音，出自同一双手',
  'home.lead': '一份关于氛围编程、音乐与视觉实验的私人日记。没有信息流，没有推荐——只有已经做出来并记录下来的东西。',
  'home.seeProjects': '查看项目',
  'home.listen': '试听',
  'home.nowPlaying': '正在播放',
  'home.inProgress': '进行中',
  'home.lastText': '最新文章',
  'home.noPosts': '暂无内容。',
  'home.guideTexts': '文章',
  'home.guideClips': '影片',
  'home.guideWorks': '作品',
  'home.guideTextsHint': '拆解与笔记',
  'home.guideClipsHint': '音乐与影像',
  'home.guideWorksHint': '工具与实验',
  'home.latestTexts': '最新文章',
  'home.allJournal': '全部日志 →',
  'home.clips': '影片',
  'home.allClips': '全部影片 →',
  'home.noTexts': '暂无文章。',
  'home.new': '新',

  'blog.title': '日志',
  'blog.lead': '关于过程的笔记：什么坏掉了，什么终于对了，以及我为什么重写它。',
  'blog.all': '全部',
  'blog.empty': '没有找到内容',
  'blog.records': '篇',
  'blog.sortNew': '最新 ↓',
  'blog.sortOld': '最早 ↑',

  'article.back': '← 返回日志',
  'article.share': '分享',
  'article.notFound': '文章不存在',
  'article.notFoundText': '该文章不存在或已被删除。',
  'article.backToBlog': '返回日志',
  'article.cover': '封面',
  'article.machineTranslated': '机器翻译，原文为俄语。',
  'article.readOriginal': '阅读原文',

  'common.min': '分钟',
  'common.loading': '加载中…',
  'common.language': '语言',
  'footer.nav': '按 / 导航',
}

const DICTS: Record<LanguageCode, Dict> = { ru: RU, en: EN, zh: ZH }

export interface UseI18nResult {
  t: (key: string) => string
  language: ComputedRef<LanguageCode>
}

export function useI18n(): UseI18nResult {
  const { current } = useLanguage()

  const dict = computed(() => DICTS[current.value] ?? DICTS[DEFAULT_LANGUAGE])

  // Отсутствующий ключ отдаём из русского словаря, а не показываем сам ключ:
  // недопереведённая строка на родном языке читается, «home.title» — нет.
  return {
    t: (key: string) => dict.value[key] ?? DICTS[DEFAULT_LANGUAGE][key] ?? key,
    language: current,
  }
}
